// notify.ts — sound + haptic alert when an agent turn finishes.
//
// The gating rule and the busy→idle edge detection are pure (and unit-tested);
// the WebAudio chime and vibration are side-effecting and device-checked.

// shouldNotify decides whether a finished turn should chime. Pure so the rule is
// testable. We only alert when sound is enabled AND the user isn't actively
// watching the tab (hidden or unfocused) — no point chiming at someone already
// looking at Claude work.
export function shouldNotify(opts: { soundOn: boolean; hidden: boolean }): boolean {
  return opts.soundOn && opts.hidden;
}

// turnJustFinished detects the busy→idle edge of a turn (was working, now done).
// Pure + tested; used so we fire exactly one notification per completed turn.
export function turnJustFinished(prevBusy: boolean, nextBusy: boolean): boolean {
  return prevBusy && !nextBusy;
}

let ctx: AudioContext | null = null;

// primeAudio lazily creates/resumes the AudioContext. Browsers block audio until
// a user gesture, so call this from the first pointerdown; later chimes can then
// sound even when the tab is backgrounded. Safe to call repeatedly.
export function primeAudio(): void {
  try {
    if (!ctx) {
      const Ctor =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      ctx = new Ctor();
    }
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    // Audio unavailable (old browser / autoplay policy) — degrade silently.
  }
}

// playChime plays a short, pleasant two-note WebAudio chime (no asset file). If
// the AudioContext hasn't been primed by a user gesture yet, it no-ops.
export function playChime(): void {
  if (!ctx || ctx.state !== 'running') return;
  const now = ctx.currentTime;
  // Two ascending notes (E6 → A6) with a soft attack/decay envelope.
  playNote(ctx, 1318.51, now, 0.18);
  playNote(ctx, 1760.0, now + 0.13, 0.24);
}

function playNote(ac: AudioContext, freq: number, start: number, dur: number): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  // Exponential ramps can't reach 0, so bracket with a tiny epsilon.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

// notifyTurnDone fires the "Claude is waiting for your response" alert when the
// gating rule passes: a chime plus a subtle vibration on mobile. `soundOn` comes
// from the persisted Settings toggle; visibility/focus is read here so the pure
// rule stays testable.
export function notifyTurnDone(soundOn: boolean): void {
  const hidden = document.visibilityState === 'hidden' || !document.hasFocus();
  if (!shouldNotify({ soundOn, hidden })) return;
  playChime();
  navigator.vibrate?.([60, 40, 60]);
}
